/**
 * QR source 컨텍스트 배너 (#365)
 *
 * 메뉴 목록 페이지 (BrandDetailPage / OrganizationDetailPage) 상단에
 * 현재 위치 앵커의 source 를 시각적으로 알려준다.
 *
 * 분기:
 * - `qr-table` + 동일 매장 진입: 테이블 오더 컨텍스트 유지 안내 (orange)
 * - `qr-table` + 다른 매장 진입: 픽업 폴백 안내 — 명시적 분기로 사용자 혼동 차단 (amber)
 * - `qr-pickup`: 픽업 매장 위치 기준 안내 (slate)
 *
 * 박제 D 단일 출처 + Karpathy 외과적:
 * - source / storeId 비교 로직 외 추가 상태 도입 없음.
 * - 만료 / 정리 등 상태 변경은 모두 `locationAnchor.ts` 가 담당.
 *
 * admin#1391 (BE 미들웨어 source 검증) cross-ref:
 * - FE 도 source 정합 강도를 강제해 위변조 가능성 표면을 줄인다.
 */

import { Info, MapPin, Utensils } from 'lucide-react';
import type { LocationAnchor } from '@/lib/locationAnchor';
import type { Language, Locale } from '@/config/locales';
import { useLanguage } from '@/contexts/AppContext';

/** 배너 문구 문자열 */
interface BannerText {
  tableContextMaintained: string;
  pickupFallbackForOtherStore: string;
  qrPickupLocationGuide: string;
}

// #522 — Locale 단일 출처 정합 (15언어 전수, 키 누락 시 컴파일 에러).
const BANNER_TEXT:
  & Record<Language, BannerText>
  & Record<Exclude<Locale, Language>, BannerText> = {
  ko: {
    tableContextMaintained: '테이블 오더 컨텍스트가 유지되고 있어요.',
    pickupFallbackForOtherStore: '테이블 QR 매장과 다른 매장이에요. 이 매장은 픽업 주문으로 진행됩니다.',
    qrPickupLocationGuide: 'QR 매장 위치를 기준으로 표시하고 있어요.',
  },
  es: {
    tableContextMaintained: 'Se mantiene el contexto de orden de mesa.',
    pickupFallbackForOtherStore: 'Esta es una tienda diferente del QR de mesa. En esta tienda se continuará como pedido para recoger.',
    qrPickupLocationGuide: 'Mostrando según la ubicación de la tienda QR.',
  },
  en: {
    tableContextMaintained: 'Table order context is being maintained.',
    pickupFallbackForOtherStore: 'This is a different store from the table QR. This store proceeds as a pickup order.',
    qrPickupLocationGuide: 'Showing based on the QR store location.',
  },
  pt: {
    tableContextMaintained: 'O contexto do pedido de mesa está sendo mantido.',
    pickupFallbackForOtherStore: 'Esta é uma loja diferente da do QR de mesa. Nesta loja o pedido continuará como retirada.',
    qrPickupLocationGuide: 'Exibindo com base na localização da loja do QR.',
  },
  fr: {
    tableContextMaintained: 'Le contexte de commande à table est conservé.',
    pickupFallbackForOtherStore: 'Ce magasin est différent de celui du QR de table. Dans ce magasin, la commande se poursuivra en mode à emporter.',
    qrPickupLocationGuide: "Affichage basé sur l'emplacement du magasin du QR.",
  },
  zh: {
    tableContextMaintained: '正在保持桌位点餐上下文。',
    pickupFallbackForOtherStore: '这是与餐桌二维码不同的门店。该门店将以自取订单方式进行。',
    qrPickupLocationGuide: '正在基于二维码门店的位置显示。',
  },
  ja: {
    tableContextMaintained: 'テーブルオーダーのコンテキストが維持されています。',
    pickupFallbackForOtherStore: 'テーブルQRの店舗とは異なる店舗です。この店舗では持ち帰り注文として進みます。',
    qrPickupLocationGuide: 'QR店舗の位置を基準に表示しています。',
  },
  hi: {
    tableContextMaintained: 'टेबल ऑर्डर संदर्भ बनाए रखा जा रहा है।',
    pickupFallbackForOtherStore: 'यह टेबल QR वाले स्टोर से अलग स्टोर है। इस स्टोर में पिकअप ऑर्डर के रूप में आगे बढ़ेगा।',
    qrPickupLocationGuide: 'QR स्टोर के स्थान के आधार पर दिखाया जा रहा है।',
  },
  de: {
    tableContextMaintained: 'Der Kontext der Tischbestellung wird beibehalten.',
    pickupFallbackForOtherStore: 'Dies ist ein anderes Geschäft als das des Tisch-QR. In diesem Geschäft wird als Abholbestellung fortgefahren.',
    qrPickupLocationGuide: 'Anzeige basierend auf dem Standort des QR-Geschäfts.',
  },
  it: {
    tableContextMaintained: "Il contesto dell'ordine al tavolo viene mantenuto.",
    pickupFallbackForOtherStore: 'Questo è un negozio diverso da quello del QR del tavolo. In questo negozio si procederà come ordine da ritirare.',
    qrPickupLocationGuide: 'Visualizzazione basata sulla posizione del negozio del QR.',
  },
  ru: {
    tableContextMaintained: 'Контекст заказа за столом сохраняется.',
    pickupFallbackForOtherStore: 'Это не тот магазин, что указан в QR стола. В этом магазине заказ продолжится как заказ навынос.',
    qrPickupLocationGuide: 'Отображается на основе местоположения магазина из QR.',
  },
  ar: {
    tableContextMaintained: 'يتم الحفاظ على سياق طلب الطاولة.',
    pickupFallbackForOtherStore: 'هذا متجر مختلف عن متجر QR الطاولة. في هذا المتجر ستتم المتابعة كطلب استلام.',
    qrPickupLocationGuide: 'يتم العرض بناءً على موقع متجر QR.',
  },
  vi: {
    tableContextMaintained: 'Ngữ cảnh đặt món tại bàn đang được duy trì.',
    pickupFallbackForOtherStore: 'Đây là cửa hàng khác với cửa hàng của QR bàn. Tại cửa hàng này, đơn hàng sẽ tiếp tục dưới dạng mang đi.',
    qrPickupLocationGuide: 'Đang hiển thị dựa trên vị trí cửa hàng của QR.',
  },
  th: {
    tableContextMaintained: 'กำลังรักษาบริบทการสั่งอาหารที่โต๊ะ',
    pickupFallbackForOtherStore: 'นี่เป็นร้านที่ต่างจากร้านของ QR โต๊ะ ร้านนี้จะดำเนินการเป็นคำสั่งซื้อแบบรับเอง',
    qrPickupLocationGuide: 'กำลังแสดงตามตำแหน่งร้านของ QR',
  },
  nl: {
    tableContextMaintained: 'De context van de tafelbestelling wordt behouden.',
    pickupFallbackForOtherStore: 'Dit is een andere winkel dan die van de tafel-QR. In deze winkel gaat de bestelling verder als afhaalbestelling.',
    qrPickupLocationGuide: 'Weergave op basis van de locatie van de QR-winkel.',
  },
};

import { a11yTranslations } from '@/i18n/a11y';

interface QrSourceBannerProps {
  /** 현재 위치 앵커 (없으면 렌더 X) */
  anchor: LocationAnchor | null;
  /**
   * 현재 페이지의 매장 ID (선택).
   * - 미지정: 단순 source 표시 (조직/브랜드 목록 등 매장 비특정 페이지)
   * - 지정: 앵커 storeId 와 비교해 동일/다른 매장 분기 표시
   */
  currentStoreId?: number | null;
}

export function QrSourceBanner({
  anchor,
  currentStoreId,
}: QrSourceBannerProps): JSX.Element | null {
  // #522: 15 locale 전수 — language(3종) 대신 locale 로 직접 조회
  const { locale } = useLanguage();
  const a11y = a11yTranslations[locale];
  const banner = BANNER_TEXT[locale] ?? BANNER_TEXT.es;

  if (anchor === null) return null;

  const isTable = anchor.source === 'qr-table';
  const isSameStore =
    typeof currentStoreId === 'number' && currentStoreId === anchor.storeId;

  if (isTable && isSameStore) {
    return (
      <div
        role="status"
        aria-label={a11y.qrTableContextMaintained}
        className="mx-[min(5vw,1.25rem)] mt-[min(3vw,0.75rem)] flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-800 dark:border-orange-800/50 dark:bg-orange-950/30 dark:text-orange-200"
      >
        <Utensils className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{banner.tableContextMaintained}</span>
      </div>
    );
  }

  if (isTable) {
    // qr-table 진입했으나 다른 매장 목록 — 다른 매장은 픽업으로만 진행됨
    return (
      <div
        role="status"
        aria-label={a11y.qrPickupFallbackForOtherStore}
        className="mx-[min(5vw,1.25rem)] mt-[min(3vw,0.75rem)] flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100"
      >
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{banner.pickupFallbackForOtherStore}</span>
      </div>
    );
  }

  // qr-pickup
  return (
    <div
      role="status"
      aria-label={a11y.qrPickupLocationGuide}
      className="mx-[min(5vw,1.25rem)] mt-[min(3vw,0.75rem)] flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
    >
      <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{banner.qrPickupLocationGuide}</span>
    </div>
  );
}
